// HistogramWithCutoffs.js
import React from 'react';
import Plot from 'react-plotly.js';

const HistogramWithCutoffs = ({ data, cutPointSens, cutPointSpec, cutPointYouden }) => {
  const classifications = [...new Set(data.map(row => row.positivity))];
  const colors_histograms = ["#F0E8DB", "#DBC49B"]; // Adjust transparency
  const cutoff_colors = ['#B6BEC1', '#768B8C', '#2E3C3D'];

  // Prepare density traces
  const traces = classifications.map((classLabel, index) => {
    const subsetData = data.filter(row => row.positivity === classLabel);
    const values = subsetData.map(row => row.value);

    // Compute density
    const density = computeDensity(values);

    return {
      x: density.x,
      y: density.y,
      type: 'scatter',
      mode: 'lines',
      fill: 'tozeroy',
      fillcolor: (classLabel === 0 ? colors_histograms[0] : colors_histograms[1]) + "90",
      line: { color: (classLabel === 0 ? colors_histograms[0] : colors_histograms[1]) },
      name: classLabel === 0 ? 'Negative' : 'Positive',
    };
  });

  traces.sort((a, b) => a.name === 'Positive' ? 1 : -1);

  // Add cutoff lines
  const cutoffs = [
    { label: 'Conservative Threshold', value: cutPointSens, color: cutoff_colors[0] },
    { label: 'Intermediate Threshold', value: cutPointYouden, color: cutoff_colors[1] },
    { label: 'Liberal Threshold', value: cutPointSpec, color: cutoff_colors[2] },
  ];
  const cutoffTraces = cutoffs.map(cutoff => ({
    x: [cutoff.value, cutoff.value],
    y: [0, 2.5],
    mode: 'lines',
    name: cutoff.label,
    line: { color: cutoff.color, width: 2 },
  }));

  // Combine traces
  const plotData = [...traces, ...cutoffTraces];

  return (
    <div style={{ width: '100%' }}>
      <Plot
        data={plotData}
        layout={{
          title: 'Density Plot with Thresholds',
          yaxis: { title: 'Density' },
          margin: { l: 45, r: 0, b: 0, t: 45},
          legend: { orientation: 'h', font: { family: 'Roboto', size: 13 } },
          xaxis: {
            title: {
              text: 'Value',
              standoff: 8,
            },
          },
          autosize: true,
        }}
        config={{ responsive: true }}
        useResizeHandler
        style={{ width: '100%', height: '450px' }}
      />
    </div>
  );
};

// Compute density using Gaussian KDE
const computeDensity = (values) => {
  const { min, max, linspace } = numericUtils();
  const kde = new KernelDensityEstimator(values);
  const x = linspace(min(values), max(values), 100);
  const y = kde.estimate(x);
  return { x, y };
};

// Helper functions
const numericUtils = () => {
  const min = arr => Math.min(...arr);
  const max = arr => Math.max(...arr);
  const linspace = (start, stop, num) => {
    const arr = [];
    const step = (stop - start) / (num - 1);
    for (let i = 0; i < num; i++) {
      arr.push(start + step * i);
    }
    return arr;
  };
  return { min, max, linspace };
};

// Kernel Density Estimator Class
class KernelDensityEstimator {
  constructor(values, bandwidth = 0.2) {
    this.values = values;
    this.bandwidth = bandwidth;
  }

  gaussian(x) {
    return (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x);
  }

  estimate(points) {
    const n = this.values.length;
    const density = new Array(points.length).fill(0);
    for (let i = 0; i < points.length; i++) {
      for (let j = 0; j < n; j++) {
        density[i] += this.gaussian((points[i] - this.values[j]) / this.bandwidth);
      }
      density[i] /= (n * this.bandwidth);
    }
    return density;
  }
}

export default HistogramWithCutoffs;
